<template>
  <div class="cases-page">
    <section class="cases-hero reveal">
      <div class="cases-hero__media ratio-9x3">
        <video
          v-if="page.hero.video"
          class="cases-hero__video"
          :poster="page.hero.poster"
          :src="page.hero.video"
          autoplay
          muted
          loop
          playsinline
        ></video>
        <img v-else class="cases-hero__image" :src="page.hero.poster" alt="">
      </div>
      <div class="cases-hero__overlay"></div>
      <div class="cases-hero__content container">
        <div class="cases-hero__title-wrap">
          <p class="cases-hero__pretitle">{{ page.hero.pretitle }}</p>
          <h1 class="cases-hero__title">{{ page.hero.title }}</h1>
        </div>
      </div>
    </section>

    <page-trail
      :items="[
        { label: $t('common.home'), to: '/' },
        { label: $t('nav.strength'), to: '/strength' },
        { label: $t('common.cases'), to: '/cases' }
      ]"
      :back-label="$t('common.backToStrength')"
      back-to="/strength"
    />

    <section class="cases-list reveal">
      <div class="container">
        <div class="cases-grid">
          <article
            v-for="(item, index) in page.items"
            :key="item.title + '-' + index"
            class="cases-teaser"
          >
            <div class="cases-teaser__image ratio-4x3">
              <img :src="item.image" :alt="item.title" loading="lazy">
            </div>
            <h3 class="cases-teaser__title">{{ item.title }}</h3>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import site from '../mock/strongova'
import PageTrail from '../components/PageTrail.vue'
import { fetchPublicCases, normalizeCase } from '../api/public'

export default {
  name: 'CasesPage',
  components: {
    PageTrail
  },
  data () {
    return {
      page: site.casesPage
    }
  },
  created () {
    fetchPublicCases()
      .then(cases => {
      if (Array.isArray(cases) && cases.length) {
        this.page = {
          ...site.casesPage,
          items: cases.map(item => normalizeCase(item, site.casesPage.items[0] && site.casesPage.items[0].image))
        }
      }
      })
      .catch(() => {
        this.page = site.casesPage
      })
  }
}
</script>

<style>
.cases-page {
  background: #f6f1e9;
  color: var(--dark-text);
}

.ratio-9x3 {
  aspect-ratio: 9 / 3;
}

.ratio-4x3 {
  aspect-ratio: 4 / 3;
}

.cases-hero {
  position: relative;
  overflow: hidden;
  background: #0f0f0f;
}

.cases-hero__media {
  position: relative;
  width: 100%;
  min-height: clamp(15.625rem, 32vw, 28rem);
  background: #111;
}

.cases-hero__video,
.cases-hero__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cases-hero__overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background:
    linear-gradient(
      90deg,
      rgba(15, 15, 15, 0.05) 0%,
      rgba(15, 15, 15, 0.35) 42%,
      rgba(15, 15, 15, 0.88) 68%,
      rgba(15, 15, 15, 0.95) 100%
    ),
    linear-gradient(180deg, rgba(15, 15, 15, 0.08) 0%, rgba(15, 15, 15, 0.55) 100%);
}

.cases-hero__content {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  align-items: end;
  padding: clamp(2rem, 5vw, 3.5rem) 0;
}

.cases-hero__title-wrap {
  grid-column: 1;
  width: 100%;
  max-width: 22rem;
  margin: 0 auto;
  text-align: center;
  color: #f5f3ef;
}

.cases-hero__pretitle {
  margin: 0 0 0.625rem;
  font-size: 15px;
  letter-spacing: 0.08em;
  line-height: 1.55;
  text-transform: none;
  color: rgba(245, 243, 239, 0.76);
}

.cases-hero__title {
  margin: 0;
  font-size: clamp(1.875rem, 2.6vw, 2.5rem);
  font-weight: 400;
  line-height: 1.08;
  letter-spacing: 0.02em;
}

.cases-page .page-trail {
  background: #f6f1e9;
  border-bottom-color: rgba(23, 20, 17, 0.1);
}

.cases-list {
  padding: clamp(2.5rem, 4vw, 3.5rem) 0 100px;
}

.cases-list .container {
  width: min(1360px, calc(100vw - 64px));
}

.cases-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 30px;
}

.cases-teaser {
  display: block;
  min-width: 0;
  color: inherit;
}

.cases-teaser__image {
  overflow: hidden;
  background: #ddd8cf;
}

.cases-teaser__image img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.cases-teaser__title {
  margin: 0.75rem 0 0;
  font-size: 1.275rem;
  font-weight: 400;
  line-height: 1.3;
}

@media (min-width: 768px) {
  .cases-hero__content {
    align-items: center;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    column-gap: 24px;
  }

  .cases-hero__title-wrap {
    grid-column: 8 / span 5;
    max-width: none;
    margin: 0;
    text-align: left;
  }

  .cases-hero__pretitle {
    font-size: 15px;
  }
}

@media (max-width: 1680px) {
  .cases-grid {
    gap: 20px;
  }
}

@media (max-width: 1024px) {
  .cases-list .container {
    width: min(100%, calc(100vw - 48px));
  }

  .cases-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
  }

  .cases-teaser__title {
    font-size: 1.175rem;
  }
}

@media (max-width: 767px) {
  .cases-hero__media {
    min-height: clamp(14rem, 42vw, 18rem);
  }

  .cases-hero__content {
    align-items: flex-end;
  }

  .cases-hero__title-wrap {
    max-width: none;
    padding: 0 0.5rem;
  }

  .cases-hero__title {
    font-size: 1.75rem;
  }

  .cases-list {
    padding-top: 2rem;
  }

  .cases-list .container {
    width: calc(100vw - 32px);
  }

  .cases-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 15px;
  }
}

@media (max-width: 540px) {
  .cases-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .cases-teaser__title {
    font-size: 1.125rem;
  }
}
</style>
